import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { ILegalFormExtendedDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { legalFormActions } from '../../actions/settings.actions';
import { LegalFormFormComponent } from '../../components/legal-form-form/legal-form-form.component';
import { SettingsProvider } from '../../providers/settings.provider';
import { getDebug, getTag } from '../../utils/debugUtil';

const filename = 'projects/aumico-lib-settings-fe/src/lib/pages/financial-line-item/financial-line-item.page.ts';
const debug = getDebug(filename);
const TAG = getTag(filename);
@UntilDestroy()
@Component({
    // tslint:disable-next-line: component-selector
    selector: 'aumico-settings-legal-form',
    templateUrl: './legal-form.page.html',
    styleUrls: ['./legal-form.page.scss'],
})
export class LegalFormPage implements OnInit, OnDestroy {
    public data: ILegalFormExtendedDTO[];
    private currentLegalForm: ILegalFormExtendedDTO | undefined;

    private errorMessage = new Subject<HttpErrorResponse>();

    private destroy$ = new Subject<void>();

    @Input() public set injectedProviderSetter(injectedProvider: SettingsProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }

    @Input() languages: string[] = ['de-ch', 'en-us'];
    @Input() countries: INameId[] = [];

    constructor(
        private provider: SettingsProvider,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<LegalFormFormComponent>,
        protected actionsListener$: ActionsSubject,
    ) {}

    private subscriptions: Subscription[];
    ngOnInit() {
        this.subscriptions = [];
        this.subscriptions.push(
            this.provider.getActiveLegalForm$().subscribe({
                next: (item: ILegalFormExtendedDTO) => {
                    this.currentLegalForm = item;
                },
            }),
        );

        this.subscriptions.push(
            this.actionsListener$
                .pipe(ofType(legalFormActions.onSubmitCreateLegalFormSuccess), untilDestroyed(this))
                .subscribe(() => this.onSaveSuccessfully()),
        );
        this.subscriptions.push(
            this.actionsListener$
                .pipe(ofType(legalFormActions.onSubmitEditLegalFormSuccess), untilDestroyed(this))
                .subscribe(() => this.onSaveSuccessfully()),
        );

        this.subscriptions.push(this.provider.allLegalForm$().subscribe({ next: (data) => (this.data = data) }));
        this.provider.dispatchGetAllLegalForm();

        this.provider.legalFormError$().pipe(untilDestroyed(this)).subscribe(this.errorMessage);
    }

    public editPressedEventHandler(item: string): void {
        this.provider.dispatchSetActiveLegalForm(item);
        this.openDialog();
    }

    onCreateLegalFormPressed() {
        this.provider.dispatchSetActiveLegalForm(null);
        this.openDialog();
    }

    openDialog(): void {
        this.dialogRef = this.dialog.open(LegalFormFormComponent, {
            width: '70%',
            data: { legalForm: this.currentLegalForm, errorMessage: this.errorMessage, countries: this.countries },
        });
        this.dialogRef.componentInstance.languages = this.languages;
        this.dialogRef.componentInstance.savePressed.subscribe((result) => {
            if (result._id == null) {
                this.provider.dispatchSubmitCreateLegalForm(result);
            } else {
                this.provider.dispatchSubmitUpdateLegalForm({
                    id: result._id,
                    payload: { text: result.text, countryISO31661Alpha2Codes: result.countryISO31661Alpha2Codes },
                });
            }
        });

        this.dialogRef.afterClosed().subscribe(() => {
            // destroying subscriptions that were created upon opening the dialog
            this.destroyDialogSubscriptions();
        });
    }

    private onSaveSuccessfully() {
        this.dialogRef.close();
        Swal.fire({
            icon: 'info',
            title: 'done editing/creating',
            showCloseButton: true,
            timer: 2000,
        });
        this.provider.dispatchGetAllLegalForm();
    }

    destroyDialogSubscriptions(): void {
        this.destroy$.next();
    }

    ngOnDestroy(): void {
        this.destroyDialogSubscriptions();
        this.destroy$.complete();
        this.subscriptions.forEach((subscribe) => {
            subscribe.unsubscribe();
        });
    }
}
